import { MutationTree } from 'vuex';
import { EvaluatorState } from './types';
import { GoalResource, PackageResource, RatingResource } from '@/api/requests/evaluator/types';
import { SingleFilter } from '@/store/types';
import { GoalMediaDeleteRequestPayload, GoalMediaResourcePayload, PackageMediaDeleteRequestPayload, PackageMediaResourcePayload } from '@/store/modules/evaluator/types';

export const mutations: MutationTree<EvaluatorState> = {
    
    /** Goals */

    /**
     * Add a goal to the list.
     *
     * @param state
     * @param goal
     */
    addGoal(state, goal: GoalResource) {
        state.goals.push(goal);
    },

    /**
     * Remove an goal from the list.
     *
     * @param state
     * @param goalId
     */
    removeGoal(state, goalId: number) {
        state.goals = state.goals.filter((goal) => goal.id !== goalId);
    },

    /**
     * Set the goals list.
     *
     * @param state
     * @param goals
     */
    setGoals(state, goals: GoalResource[]) {
        state.goals = goals;
    },

    /**
     * Reset goals
     *
     * @param state
     */
    resetGoals(state) {
        state.goals = [];
    },

    /**
     * Set the active goal id.
     *
     * @param state
     * @param id
     */
     setActiveGoalId(state, id: number) {
        state.activeGoalId = id;
    },

    /**
     * Update a goal.
     *
     * @param state
     * @param goal
     */
    updateGoal(state, goal: GoalResource) {
        const index = state.goals.findIndex((item) => item.id === goal.id);
        state.goals.splice(index, 1, goal);
    },

    /**
     * Update a goal.
     *
     * @param state
     * @param goal
     */
    upsertGoal(state, goal: GoalResource) {
        const index = state.goals.findIndex((item) => item.id === goal.id);
        if (index > -1) {
            state.goals.splice(index, 1, goal);
        } else {
            state.goals.push(goal);
        }
    },

    /** Packages */

    /**
     * Add a package to the list.
     *
     * @param state
     * @param package
     */
     addPackage(state, singlePackage: PackageResource) {
        state.packages.push(singlePackage);
    },

    /**
     * Remove a package from the list.
     *
     * @param state
     * @param packageId
     */
    removePackage(state, packageId: number) {
        state.packages = state.packages.filter((singlePackage) => singlePackage.id !== packageId);
    },

    /**
     * Set the packages list.
     *
     * @param state
     * @param packages
     */
    setPackages(state, packages: PackageResource[]) {
        state.packages = packages;
    },


    /**
     * Reset packages
     *
     * @param state
     */
    resetPackages(state) {
        state.packages = [];
    },

    /**
     * Update a package.
     *
     * @param state
     * @param packages
     */
    updatePackage(state, singlePackage: PackageResource) {
        const index = state.packages.findIndex((item) => item.id === singlePackage.id);
        state.packages.splice(index, 1, singlePackage);
    },

    /**
     * Update a package. (???????)
     *
     * @param state
     * @param package
     */
    upsertPackage(state, singlePackage: PackageResource) {
        const index = state.packages.findIndex((item) => item.id === singlePackage.id);
        if (index > -1) {
            state.packages.splice(index, 1, singlePackage);
        } else {
            state.packages.push(singlePackage);
        }
    },

    /** Ratings */

    /**
     * Set the ratings list.
     *
     * @param state
     * @param ratings
     */
    setRatings(state, ratings: RatingResource[]) {
        state.ratings = ratings;
    },

    /** Filters */

    /**
     * Set a query filter in state.
     *
     * @param state
     * @param key
     * @param value
     */
    setFilter(state, { key, value }: SingleFilter) {
        state.filters = { ...state.filters, [key]: value };
    },

    /**
     * Reset a single filter.
     *
     * @param state
     * @param key
     */
    resetFilter(state, key: string) {
        delete state.filters[key];
    },

    /* Goal Media */

    /**
     * Add a media to a goal, or update if already exists.
     *
     * @param state
     * @param goalId
     * @param media
     */
    addMediaToGoal(state, { goalId, media }: GoalMediaResourcePayload) {
        const goal = state.goals.find((item) => item.id === goalId);
        if (!goal) {
            return;
        }
        const index = goal && goal.media.length > 0
            ? goal.media.findIndex((item) => item.id === media.id)
            : -1;
        if (index > -1) {
            goal.media.splice(index, 1, media);
        } else {
            goal.media.push(media);
        }
    },

    /**
     * Remove a media from a goal.
     *
     * @param state
     * @param goalId
     * @param mediaId
     */
    removeMediaFromGoal(state, { goalId, mediaId }: GoalMediaDeleteRequestPayload) {
        const goal = state.goals.find((item) => item.id === goalId);
        if (!goal) {
            return;
        }
        const index = goal && goal.media.length > 0
            ? goal.media.findIndex((item) => item.id === mediaId)
            : -1;
        if (index > -1) {
            goal.media.splice(index, 1);
        }
    },

    /* Package Media */

    /**
     * Add a media to a package, or update if already exists.
     *
     * @param state
     * @param packageId
     * @param media
     */
     addMediaToPackage(state, { packageId, media }: PackageMediaResourcePayload) {
        const singlePackage = state.packages.find((item) => item.id === packageId);
        if (!singlePackage) {
            return;
        }
        const index = singlePackage && singlePackage.media.length > 0
            ? singlePackage.media.findIndex((item) => item.id === media.id)
            : -1;
        if (index > -1) {
            singlePackage.media.splice(index, 1, media);
        } else {
            singlePackage.media.push(media);
        }
    },

    /**
     * Remove a media from a package.
     *
     * @param state
     * @param packageId
     * @param mediaId
     */
    removeMediaFromPackage(state, { packageId, mediaId }: PackageMediaDeleteRequestPayload) {
        const singlePackage = state.packages.find((item) => item.id === packageId);
        if (!singlePackage) {
            return;
        }
        const index = singlePackage && singlePackage.media.length > 0
            ? singlePackage.media.findIndex((item) => item.id === packageId)
            : -1;
        if (index > -1) {
            singlePackage.media.splice(index, 1);
        }
    },
    
};
